/**
 * What deleting a vault means to bill for and to hope for, per destination —
 * the retention and recoverability sentences the desktop app's Delete
 * dialog will show beside its warning (the dialog itself is a later ticket),
 * and the anchor of the Help guide section that walks through doing the same
 * thing by hand on that provider.
 *
 * Every sentence here describes **the app's own Delete**, not a delete the
 * user performs at the provider's console. The two differ, and on three
 * providers they differ in the direction that costs money: the app's Drive
 * delete bypasses Trash, a folder dragged to the bin by hand does not; the
 * app deletes S3 objects by version id, a console delete by name leaves the
 * versions underneath. Anything rendering these sentences beside a
 * by-hand guide has to say which of the two it is describing — see
 * `apps/portal/src/help/DeleteVaultProviderNoteTable.tsx`.
 *
 * `"s3"` covers every S3-compatible destination, from Backblaze B2 to a
 * self-hosted MinIO, and the honest answer differs by provider — Backblaze
 * has no minimum retention, Wasabi bills the remainder of a 90-day one, and
 * one the app doesn't recognise might have either. So for `"s3"` the note
 * is also keyed by the endpoint host, sniffed the same coarse way a browser
 * sniffs a redirect target: a suffix match, not a lookup against a list of
 * everything the app has heard of.
 *
 * One table, read by the dialog and rendered as a page by
 * `apps/portal/src/help/guides/delete-a-vault-by-hand.md`'s table, so app
 * and site cannot say different things about the same provider.
 */

import type { DestinationKind } from "./types";

/**
 * The Help guide section a note's `guideAnchor` points at, by the anchor
 * `github-slugger` gives that section's heading in
 * `delete-a-vault-by-hand.md` — `HelpGuide.test.tsx` pins these same six
 * strings against the rendered page, so a heading reworded there without
 * updating here fails loudly instead of quietly 404ing an in-page link.
 */
export const DELETE_VAULT_GUIDE_ANCHOR_DRIVE = "a-drive-or-network-share";
export const DELETE_VAULT_GUIDE_ANCHOR_BACKBLAZE = "backblaze-b2";
export const DELETE_VAULT_GUIDE_ANCHOR_S3_COMPATIBLE = "another-s3-compatible-provider";
export const DELETE_VAULT_GUIDE_ANCHOR_GOOGLE_DRIVE = "google-drive";
export const DELETE_VAULT_GUIDE_ANCHOR_ONEDRIVE = "onedrive";
export const DELETE_VAULT_GUIDE_ANCHOR_DROPBOX = "dropbox";

export interface VaultDeleteProviderNote {
  /** What deleting does to the bill. Static text, never a dollar estimate —
   * this table has no idea how much a vault holds. */
  readonly retention: string;
  /** What deleting does to the data itself: gone for good, or recoverable
   * for a while somewhere only the provider controls. */
  readonly recoverability: string;
  /** The `delete-a-vault-by-hand` guide section that walks through doing
   * this by hand on this provider. */
  readonly guideAnchor: string;
}

/**
 * The one caveat ADR 0002 attaches to the version sweep: "Where a server
 * refuses to list versions, the sweep falls back to by-name deletes and says
 * that versions may remain." Stating the sweep without it would promise
 * unconditionally what the ADR says can fail — on a bucket that hides rather
 * than removes, that is the difference between a clean delete and a bill
 * that never stops.
 */
const EVERY_VERSION_SWEPT =
  "Every stored version is deleted, so nothing is left behind to recover — unless the provider will not list versions, which the app tells you if it happens.";

const NO_BILLING_CONSEQUENCE = "There is no minimum storage duration to consider.";

/**
 * The provider note for `kind`, and for `"s3"`, for the bare endpoint host
 * the vault's credentials point at (`s3.us-west-004.backblazeb2.com`, say —
 * a host, not a full URL). An absent or unrecognised host gets the same
 * generic note any S3-compatible provider the app has never heard of would.
 */
export function vaultDeleteProviderNote(
  kind: DestinationKind,
  endpointHost?: string,
): VaultDeleteProviderNote {
  switch (kind) {
    case "local":
    case "network":
      return {
        retention: NO_BILLING_CONSEQUENCE,
        recoverability: "Files are deleted permanently, not sent to the Recycle Bin.",
        guideAnchor: DELETE_VAULT_GUIDE_ANCHOR_DRIVE,
      };
    case "google-drive":
      return {
        retention: NO_BILLING_CONSEQUENCE,
        recoverability: "Deletion is permanent — there is no trash to check afterwards.",
        guideAnchor: DELETE_VAULT_GUIDE_ANCHOR_GOOGLE_DRIVE,
      };
    case "onedrive":
      return {
        retention: NO_BILLING_CONSEQUENCE,
        recoverability:
          "The deleted folder goes to OneDrive's recycle bin, where it may still count against your quota until it's emptied.",
        guideAnchor: DELETE_VAULT_GUIDE_ANCHOR_ONEDRIVE,
      };
    case "dropbox":
      return {
        retention: NO_BILLING_CONSEQUENCE,
        recoverability:
          "The deleted folder is recoverable in Dropbox for a period before it's gone for good.",
        guideAnchor: DELETE_VAULT_GUIDE_ANCHOR_DROPBOX,
      };
    case "s3":
      return s3ProviderNote(endpointHost);
  }
}

/** `host` is `suffix` itself, or ends with it as a whole label — so
 * `s3.us-west-004.backblazeb2.com` matches `backblazeb2.com` but
 * `notbackblazeb2.com` does not. */
function hostEndsWith(host: string, suffix: string): boolean {
  return host === suffix || host.endsWith(`.${suffix}`);
}

function s3ProviderNote(endpointHost: string | undefined): VaultDeleteProviderNote {
  const host = endpointHost?.toLowerCase() ?? "";

  if (hostEndsWith(host, "backblazeb2.com")) {
    return {
      retention: "Backblaze has no minimum retention period, so deleting costs nothing.",
      recoverability: EVERY_VERSION_SWEPT,
      guideAnchor: DELETE_VAULT_GUIDE_ANCHOR_BACKBLAZE,
    };
  }

  if (hostEndsWith(host, "wasabisys.com")) {
    return {
      retention:
        "Wasabi bills a 90-day minimum retention per object — deleting early still bills the remainder of those 90 days.",
      recoverability: EVERY_VERSION_SWEPT,
      guideAnchor: DELETE_VAULT_GUIDE_ANCHOR_S3_COMPATIBLE,
    };
  }

  if (
    hostEndsWith(host, "amazonaws.com") ||
    hostEndsWith(host, "storage.googleapis.com") ||
    hostEndsWith(host, "blob.core.windows.net")
  ) {
    return {
      retention:
        "Standard-class objects delete free; other storage classes may bill for early deletion.",
      recoverability: EVERY_VERSION_SWEPT,
      guideAnchor: DELETE_VAULT_GUIDE_ANCHOR_S3_COMPATIBLE,
    };
  }

  return {
    retention: "This provider may bill a minimum retention period — check its pricing before deleting.",
    recoverability: EVERY_VERSION_SWEPT,
    guideAnchor: DELETE_VAULT_GUIDE_ANCHOR_S3_COMPATIBLE,
  };
}
